/**
 * A game's cover, made from its name alone: the same name always draws the
 * same cover, so a game is found by shape and colour before it is read.
 *
 * The cover is a plain description (a hue shift, an angle, where the glow sits);
 * the poster turns it into CSS in the brand's one palette.
 */

/** The four tones a player's face is drawn in (covers no longer use them). */
export type CoverTone = "accent" | "info" | "special" | "value";

export const TONES: readonly CoverTone[] = ["special", "info", "value", "accent"];

export interface Cover {
  /** The initial drawn quietly behind the text. */
  letter: string;
  /** How far this game's hue sits from the brand's, in degrees: a few either way, never another family. */
  hue: number;
  /** Where the glow sits along the top edge, in percent. */
  glowX: number;
  /** The gradient's angle, in degrees. */
  angle: number;
  /** How far the initial sits from the right edge, in percent. */
  letterRight: number;
}

/** FNV-1a: small, stable across runs and engines, and spread well enough for a few dozen names. */
export function hash(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h;
}

/** A seeded stream of numbers in [0, 1): each draw is independent of what the cover does with it. */
export function stream(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Trimmed and nothing else: names that differ in case are different names and get different covers. */
function key(name: string): string {
  return name.trim();
}

/** The first letter of the name, upper-cased; "?" for a name with none. */
export function coverLetter(name: string): string {
  const first = Array.from(key(name))[0];
  return first ? first.toLocaleUpperCase("en-US") : "?";
}

export function coverFor(name: string): Cover {
  const next = stream(hash(key(name)));
  return {
    letter: coverLetter(name),
    hue: Math.round(next() * 24 - 12),
    glowX: Math.round(20 + next() * 40),
    angle: Math.round(110 + next() * 20),
    letterRight: Math.round(next() * 5 - 1),
  };
}

/** The "+" cover of the empty studio: fixed, so the first thing a new studio sees never changes. */
export const NEW_GAME_COVER: Cover = {
  letter: "+",
  hue: 0,
  glowX: 20,
  angle: 120,
  letterRight: 0,
};

/**
 * A cover: the banners' deep green to night blue, shifted a few degrees of hue and angle per game. Dark in both
 * themes, like a poster, so white type always reads.
 */
/** Which ground a cover stands on: live games glow green, games in development sit in night blue. */
export type CoverGround = "live" | "development";

export function coverBackground(c: Cover, tone: CoverGround = "live"): string {
  const h = (base: number) => String(base + c.hue);
  return tone === "development"
    ? [
        `radial-gradient(130% 160% at ${String(c.glowX)}% 0%, oklch(0.34 0.07 ${h(258)}), transparent 75%)`, // token-exempt: the night-blue ground of a game still being made, the same in both themes
        `linear-gradient(${String(c.angle)}deg, oklch(0.22 0.045 ${h(250)}), oklch(0.15 0.025 ${h(275)}))`, // token-exempt: as above
      ].join(", ")
    : [
        `radial-gradient(130% 160% at ${String(c.glowX)}% 0%, oklch(0.36 0.075 ${h(185)}), transparent 75%)`, // token-exempt: the brand band's fixed dark ground, the same in both themes
        `linear-gradient(${String(c.angle)}deg, oklch(0.22 0.05 ${h(175)}), oklch(0.16 0.03 ${h(250)}))`, // token-exempt: as above
      ].join(", ");
}

/**
 * The banners' one palette (the Overview, Players, Live events and Rooms bands): Magicweave's deep green glowing
 * from the top left, through teal, to night blue. A game's cover is this palette nudged a few degrees; a banner is the console's.
 */
export const BRAND_BAND =
  "radial-gradient(130% 160% at 20% 0%, color-mix(in oklch, var(--color-accent) 55%, oklch(0.2 0.03 190)), transparent 75%), linear-gradient(120deg, oklch(0.22 0.05 175), oklch(0.16 0.03 250))"; // token-exempt: the banner's fixed dark ground, the same in both themes so white type always reads
